import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'April 23, 2026';
const CONTACT_EMAIL = 'privacy@glowlytix.app';

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1c1917', marginBottom: '0.75rem', fontFamily: 'Georgia, serif' }}>
        {title}
      </h2>
      <div style={{ color: '#57534e', lineHeight: 1.7, fontSize: '0.9375rem' }}>{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#fafaf9', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e7e5e4', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Link to="/" style={{ color: '#a8a29e', textDecoration: 'none', fontSize: '0.875rem' }}>← Back</Link>
        <span style={{ color: '#e7e5e4' }}>|</span>
        <span style={{ fontFamily: 'Georgia, serif', fontSize: '1.125rem', color: '#1c1917', fontWeight: 600 }}>Glowlytix</span>
      </div>

      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2.5rem 1.5rem 4rem' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '2rem', fontWeight: 700, color: '#1c1917', marginBottom: '0.5rem' }}>
          Privacy Policy
        </h1>
        <p style={{ color: '#a8a29e', fontSize: '0.875rem', marginBottom: '2.5rem' }}>
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '2.5rem' }}>
          <p style={{ color: '#92400e', fontSize: '0.875rem', margin: 0, lineHeight: 1.6 }}>
            <strong>Not medical advice.</strong> Glowlytix is a personal wellness tracking tool. Nothing in this app — including AI-generated insights — constitutes medical advice, diagnosis, or treatment. Always consult a qualified dermatologist or healthcare professional for skin concerns.
          </p>
        </div>

        <Section title="1. Who We Are">
          <p>
            Glowlytix ("we", "us", "our") operates the Glowlytix skin-tracking application and website at glowlytix.app. We are committed to protecting your personal data and your right to privacy.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            For questions about this policy, contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#7c3aed' }}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="2. Data We Collect">
          <p>We collect the following categories of data when you use Glowlytix:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.75rem', spaceY: '0.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong>Account data:</strong> your email address and a hashed (bcrypt) version of your password.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Skin log entries:</strong> daily glow scores, photos (stored as Base64 in our database), tags, water intake, skincare routines, treatments, supplements, makeup notes, menstrual cycle data, and free-text notes.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>AI insights:</strong> summaries generated by OpenAI's API based on your log data (see Section 5).</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Subscription data:</strong> your selected plan, billing status, and (when payment is enabled) Stripe customer and subscription identifiers. We do not store raw payment card numbers.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Usage analytics (optional):</strong> anonymous, aggregated app-usage data if you opt in via Privacy Settings.</li>
          </ul>
        </Section>

        <Section title="3. How We Store Your Data">
          <p>
            Your data is stored in a PostgreSQL database hosted on a private VPS server. Photos are stored as Base64-encoded values within your entry records — they are not hosted on a public file server. We do not use iCloud, Google Drive, or any other consumer cloud storage.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            Data is transmitted over HTTPS. Database access is restricted by firewall rules and password authentication. We do not encrypt data at rest beyond standard disk-level protections provided by our hosting environment.
          </p>
        </Section>

        <Section title="4. How We Use Your Data">
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>To provide and maintain the Glowlytix service.</li>
            <li style={{ marginBottom: '0.5rem' }}>To generate AI-powered skin insights on your request.</li>
            <li style={{ marginBottom: '0.5rem' }}>To manage your subscription and billing.</li>
            <li style={{ marginBottom: '0.5rem' }}>To send transactional notifications (e.g., reminder alerts) where you have granted permission.</li>
            <li style={{ marginBottom: '0.5rem' }}>To improve the app through aggregated, anonymous analytics if you have opted in.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            We do not sell, rent, or trade your personal data to any third party.
          </p>
        </Section>

        <Section title="5. AI Processing and OpenAI">
          <p>
            When you request AI insights, your sanitized skin-log data (glow scores, routines, tags, cycle phase, water intake) is sent to OpenAI's API for processing. This is subject to{' '}
            <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>OpenAI's Privacy Policy</a>. We do not send your photos to OpenAI by default; photo analysis, if enabled, sends a photo to OpenAI's vision API with your explicit action.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            AI-generated insights are for informational purposes only and do not constitute medical advice.
          </p>
        </Section>

        <Section title="6. Cookies and Local Storage">
          <p>
            Glowlytix stores a JWT authentication token in your browser's <code>localStorage</code> to keep you signed in. We also store your subscription plan state, reminder preferences, and locally cached AI responses in <code>localStorage</code>. This data exists only on your device. We do not use third-party tracking cookies.
          </p>
        </Section>

        <Section title="7. Your Rights (GDPR / CCPA)">
          <p>Depending on your location, you have the following rights regarding your personal data:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong>Right of access:</strong> you may request a copy of all data we hold about you.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Right to data portability:</strong> you may export your data in JSON format from Settings → Export Data, or by contacting us.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Right to erasure:</strong> you may delete your account and all associated data at any time from Settings → Privacy → Delete All Data. This action is immediate and irreversible.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Right to rectification:</strong> you may update your account email by contacting us.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Right to object:</strong> you may opt out of anonymous analytics at any time in Privacy Settings.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            To exercise any right not available in-app, email us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#7c3aed' }}>{CONTACT_EMAIL}</a>. We will respond within 30 days.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            Glowlytix is not directed at children under 13 years of age. We do not knowingly collect personal data from children under 13. If you believe a child has provided us with personal information, contact us immediately at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#7c3aed' }}>{CONTACT_EMAIL}</a> and we will delete it.
          </p>
        </Section>

        <Section title="9. Data Retention">
          <p>
            We retain your data for as long as your account is active. If you delete your account, all your data is permanently deleted from our servers immediately. Backups may retain your data for up to 30 days following deletion, after which it is purged.
          </p>
        </Section>

        <Section title="10. Third-Party Services">
          <p>We use the following third-party services that may process your data:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong>OpenAI</strong> — AI insight generation (see Section 5)</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Stripe</strong> — payment processing for paid subscriptions (no card data touches our servers)</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Cloudflare</strong> — DNS and CDN services (processes IP addresses and request metadata)</li>
          </ul>
        </Section>

        <Section title="11. Security">
          <p>
            We implement reasonable technical safeguards including HTTPS encryption in transit, bcrypt password hashing, JWT-based authentication, and server-side firewall restrictions. No method of transmission over the internet is 100% secure. In the event of a data breach affecting your personal information, we will notify you as required by applicable law.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by updating the effective date at the top of this page. Continued use of Glowlytix after changes are posted constitutes your acceptance of the updated policy.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>
            Glowlytix, Inc.<br />
            Brooklyn, NY<br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#7c3aed' }}>{CONTACT_EMAIL}</a>
          </p>
        </Section>

        <div style={{ borderTop: '1px solid #e7e5e4', paddingTop: '1.5rem', marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/terms" style={{ color: '#7c3aed', fontSize: '0.875rem' }}>Terms of Service</Link>
          <Link to="/" style={{ color: '#a8a29e', fontSize: '0.875rem' }}>Back to Glowlytix</Link>
        </div>
      </div>
    </div>
  );
}
